// Pure-function notification delivery policy.
//
// Ported from NotificationDeliveryPolicy.swift (iOS Stoat/E2EE).
//
// `decide()` maps the combination of a thread's effective delivery mode and
// whether the incoming message mentions the current user into one of four
// delivery decisions.
//
// This function is intentionally side-effect-free: it takes two inputs and
// returns a discriminated string literal. No store access, no I/O.
//
// Decision table (mirrors iOS exactly):
//
//   mode          | hasMention | decision
//   ------------- | ---------- | --------------------
//   all           | true       | deliver
//   all           | false      | deliver
//   mentionsOnly  | true       | deliver
//   mentionsOnly  | false      | mentionOnlyFiltered
//   muted         | true       | suppressMuted
//   muted         | false      | suppressMuted
//   none          | true       | suppressNoMode
//   none          | false      | suppressNoMode
//
// Note: `mode` should be the *effective* mode returned by
// `NotificationThreadSettingsStore.get()`, which already handles the
// lazy expired-mute revert (so an expired mute arrives here as 'all').

import type { NotificationDeliveryMode } from "./NotificationThreadSettings";

/**
 * Delivery decision for a single incoming notification event.
 *
 *   deliver               — show the notification
 *   suppressMuted         — suppressed because the thread is muted
 *   suppressNoMode        — suppressed because mode is 'none'
 *   mentionOnlyFiltered   — suppressed because mode is mentionsOnly and
 *                           the message does not mention the user
 */
export type NotificationDeliveryDecision =
  | "deliver"
  | "suppressMuted"
  | "suppressNoMode"
  | "mentionOnlyFiltered";

/**
 * Decide whether a notification should be delivered.
 *
 * @param threadMode  The effective delivery mode for the channel.
 *                    Pass the value returned by
 *                    `NotificationThreadSettingsStore.get()` to get correct
 *                    lazy-expired-mute behaviour.
 * @param hasMention  Whether the incoming message mentions the current user.
 */
export function decide(
  threadMode: NotificationDeliveryMode,
  hasMention: boolean,
): NotificationDeliveryDecision {
  switch (threadMode) {
    case "all":
      return "deliver";

    case "mentionsOnly":
      return hasMention ? "deliver" : "mentionOnlyFiltered";

    case "muted":
      return "suppressMuted";

    case "none":
      return "suppressNoMode";
  }
}
